import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';
import { getStatusBadgeClass } from '../../../../constants/statusColors';
import { appToast } from '../../../../components/AppToast';

export default function SearchEmployeeTable({
  records = [],
  isLoading = false,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-xs font-semibold text-on-surface-variant">Searching employee directory...</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-64 w-full flex-col items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8 text-center">
        <span className="material-symbols-outlined mb-2 text-4xl text-outline-variant">
          person_search
        </span>
        <h3 className="font-display text-sm font-bold text-black">No Employees Found</h3>
        <p className="mt-1 text-xs text-slate-600 max-w-sm">
          No employee records matched your search query or selected filter criteria.
        </p>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    );
  }

  const copyToClipboard = (text, label) => {
    if (text) {
      navigator.clipboard.writeText(text);
      appToast.success(`Copied ${label}: ${text}`);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-outline-variant/25 bg-surface-container-low/40 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-6 text-black">EMPLOYEE NAME</th>
              <th className="py-3.5 px-6 text-black">BRANCH</th>
              <th className="py-3.5 px-6 text-black">DEPARTMENT</th>
              <th className="py-3.5 px-6 text-black">MOBILE</th>
              <th className="py-3.5 px-6 text-black">EMAIL ID</th>
              <th className="py-3.5 px-6 text-center text-black">STATUS</th>
              <th className="py-3.5 px-6 text-right w-20 text-black">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item, index) => {
              const initials = item.name
                ? item.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : 'EP';

              return (
                <tr
                  key={item.id || index}
                  className="transition-colors hover:bg-surface-container-low/50 group"
                >
                  {/* Employee Name with Avatar */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-xs shrink-0">
                        {initials}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-bold text-sm text-on-surface font-sans">
                          {item.name}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {item.employeeId && (
                            <span className="text-xs text-slate-500 font-sans tabular-nums font-semibold">
                              {item.code || `EMP-${item.id}`} ({item.employeeId})
                            </span>
                          )}
                          {item.designation && (
                            <span className="text-[11px] text-slate-500 font-medium">
                              • {item.designation}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Branch */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 rounded-md bg-surface-container-high px-2 py-0.5 text-xs font-semibold text-slate-700">
                      <span className="material-symbols-outlined text-xs text-slate-500">location_city</span>
                      <span>{item.branch || 'Headquarters'}</span>
                    </span>
                  </td>

                  {/* Department */}
                  <td className="py-4 px-6 text-xs text-slate-700 font-semibold whitespace-nowrap">
                    {item.department || 'General'}
                  </td>

                  {/* Mobile */}
                  <td className="py-4 px-6 whitespace-nowrap font-sans tabular-nums font-medium text-xs text-on-surface">
                    {item.mobile ? (
                      <div className="inline-flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-sm text-slate-400">call</span>
                        <span>{item.mobile}</span>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  {/* Email ID */}
                  <td className="py-4 px-6 whitespace-nowrap font-medium text-xs">
                    {item.email ? (
                      <a
                        href={`mailto:${item.email}`}
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                        title="Send Email"
                      >
                        <span className="material-symbols-outlined text-sm text-slate-400">mail</span>
                        <span>{item.email}</span>
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="py-4 px-6 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${getStatusBadgeClass(
                        item.status
                      )}`}
                    >
                      {item.status || 'Active'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="py-4 px-6 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1 text-[#4b5563] hover:text-black hover:bg-surface-container-high transition bg-transparent cursor-pointer"
                        items={[
                          {
                            key: 'copy-email',
                            label: 'Copy Email',
                            icon: 'content_copy',
                            onClick: () => copyToClipboard(item.email, 'Email ID'),
                          },
                          {
                            key: 'copy-mobile',
                            label: 'Copy Mobile Number',
                            icon: 'phone_iphone',
                            onClick: () => copyToClipboard(item.mobile, 'Mobile No'),
                          },
                        ]}
                        onSelect={(key) => {
                          if (key === 'copy-email') copyToClipboard(item.email, 'Email ID');
                          if (key === 'copy-mobile') copyToClipboard(item.mobile, 'Mobile No');
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
